import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import "./ChangePasswordPrompt.css";

const ChangePasswordPrompt = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    isValid: false,
    hasLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });

  useEffect(() => {
    // Redirect if not logged in
    if (!user || !token) {
      navigate("/login");
    }
  }, [user, token, navigate]);

  // Check password strength
  useEffect(() => {
    const { newPassword } = passwordData;

    setPasswordStrength({
      hasLength: newPassword.length >= 8,
      hasUpper: /[A-Z]/.test(newPassword),
      hasLower: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      isValid:
        newPassword.length >= 8 &&
        /[A-Z]/.test(newPassword) &&
        /[a-z]/.test(newPassword) &&
        /\d/.test(newPassword) &&
        /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    });
  }, [passwordData.newPassword]);

  const handleChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { currentPassword, newPassword, confirmPassword } = passwordData;

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      setLoading(false);
      return;
    }

    if (!passwordStrength.isValid) {
      setError("Password does not meet the requirements");
      setLoading(false);
      return;
    }

    try {
      // Call API to change password
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess(true);

      // After 3 seconds, log out and redirect to login
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "Failed to change password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-prompt">
      <div className="change-password-card">
        <h2>Change Your Password</h2>
        <p className="prompt-message">
          For security reasons, you need to change your temporary password
          before continuing.
        </p>

        {error && <div className="error-message">{error}</div>}

        {success ? (
          <div className="success-message">
            <p>Password changed successfully!</p>
            <p>You will be redirected to the login page in a few seconds...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handleChange}
                required
              />
            </div>

            <div className="password-strength">
              <h4>Password Requirements:</h4>
              <ul>
                <li
                  className={passwordStrength.hasLength ? "valid" : "invalid"}
                >
                  At least 8 characters
                </li>
                <li className={passwordStrength.hasUpper ? "valid" : "invalid"}>
                  At least one uppercase letter
                </li>
                <li className={passwordStrength.hasLower ? "valid" : "invalid"}>
                  At least one lowercase letter
                </li>
                <li
                  className={passwordStrength.hasNumber ? "valid" : "invalid"}
                >
                  At least one number
                </li>
                <li
                  className={passwordStrength.hasSpecial ? "valid" : "invalid"}
                >
                  At least one special character
                </li>
              </ul>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading || !passwordStrength.isValid}
            >
              {loading ? "Changing Password..." : "Change Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordPrompt;
